import { useCallback } from 'react';
import { createUnitTour, getUnitTour } from '@/features/unit-tour';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  PENDING_APPROVAL: 'Pending Approval',
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
  CLOSED: 'Closed',
};

interface UnitTourReportUnit {
  id: number;
  name: string;
  layout_name?: string | null;
}

interface UnitTourReportResult {
  generatedCount: number;
  failedUnits: string[];
}

const unitLabelFromTour = (tour: Awaited<ReturnType<typeof getUnitTour>>) =>
  tour.unit.layout_name
    ? `${tour.unit.layout_name} - ${tour.unit.name}`
    : tour.unit.name;

const unitLabelFromUnit = (unit: UnitTourReportUnit) =>
  unit.layout_name ? `${unit.layout_name} - ${unit.name}` : unit.name;

const writeReportWindow = (reportWindow: Window, html: string) => {
  reportWindow.document.open();
  reportWindow.document.write(html);
  reportWindow.document.close();
};

const qrImageUrl = (value: string) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=1&data=${encodeURIComponent(value)}`;

const loadingHtml = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Generating QR Punch Walks</title>
<style>
  body { font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif; color:#0f172a; margin:0; background:#fff; }
  .wrap { min-height:100vh; display:grid; place-items:center; padding:32px; text-align:center; }
  h1 { font-size:22px; margin:0 0 8px; }
  p { color:#475569; margin:0; }
</style>
</head>
<body><div class="wrap"><div><h1>Generating QR Punch Walks...</h1><p>This window will print when the reports are ready.</p></div></div></body>
</html>`;

/**
 * Generates a printable per-unit punch report containing a QR code that opens
 * the public virtual tour for that unit. Creates (or reuses) the tour token,
 * renders the QR, and opens a print window listing the unit's punch items.
 */
export const useUnitTourReport = () => {
  const generateUnitTourReports = useCallback(
    async (
      projectId: number,
      units: UnitTourReportUnit[],
      projectName?: string,
    ): Promise<UnitTourReportResult> => {
      if (units.length === 0) {
        return { generatedCount: 0, failedUnits: [] };
      }

      const reportWindow = window.open('', '_blank', 'width=1000,height=1200');
      if (!reportWindow) {
        throw new Error('Unable to open the report window. Check your pop-up blocker.');
      }
      writeReportWindow(reportWindow, loadingHtml);

      // Allow overriding the QR's base URL (e.g. a LAN IP for phone testing)
      // via VITE_PUBLIC_APP_URL; otherwise use the current origin.
      const baseUrl = (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined)?.replace(/\/$/, '')
        || window.location.origin;
      const logoUrl = `${window.location.origin}/static/images/IronView_Vector.svg`;
      const nowLabel = new Date().toLocaleString();
      const pages: string[] = [];
      const failedUnits: string[] = [];

      for (const unit of units) {
        try {
          const { token } = await createUnitTour(projectId, unit.id);
          const tour = await getUnitTour(token);
          const tourUrl = `${baseUrl}/unit-tour/${token}`;
          const qrDataUrl = qrImageUrl(tourUrl);
          const unitLabel = unitLabelFromTour(tour);
          const companyNames = tour.companies.map((company) => company.name).join(', ') || 'No companies assigned';

          const itemRows = tour.items.length
            ? tour.items
                .map(
                  (item) => `<tr>
        <td>${escapeHtml(item.title)}</td>
        <td>${escapeHtml(item.company_name ?? 'Unassigned')}</td>
        <td>${escapeHtml(STATUS_LABELS[item.status] ?? item.status)}</td>
      </tr>`,
                )
                .join('')
            : '<tr><td colspan="3">No punch items linked to a hotspot in this unit.</td></tr>';

          pages.push(`
  <section class="unit-page">
    <div class="header">
      <img class="logo" src="${logoUrl}" alt="Ironview logo" />
      <h1 class="title">${escapeHtml(unitLabel)}</h1>
      ${projectName ? `<div class="project-line">${escapeHtml(projectName)}</div>` : ''}
      <div class="subtitle">QR Punch Walk • Generated ${escapeHtml(nowLabel)}</div>
    </div>

    <div class="qr-wrap">
      <img src="${qrDataUrl}" alt="Scan to open the QR Punch Walk" />
      <div class="qr-instructions">
        <h2>Scan to start your QR Punch Walk</h2>
        <p>Point your phone camera at the QR code to open the interactive punch list for this unit.</p>
        <p>Select your company, walk to each marked location, complete the item, and submit a proof photo for approval.</p>
        <div class="qr-url">${escapeHtml(tourUrl)}</div>
      </div>
    </div>

    <div class="card">
      <h3>Punch Items (${tour.items.length}) • Companies: ${escapeHtml(companyNames)}</h3>
      <table>
        <thead><tr><th>Item</th><th>Company</th><th>Status</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table>
      <div class="footnote">Tip: In the print dialog, choose "Save as PDF".</div>
    </div>
  </section>`);
        } catch {
          failedUnits.push(unitLabelFromUnit(unit));
        }
      }

      if (pages.length === 0) {
        writeReportWindow(reportWindow, `<!doctype html>
<html>
<head><meta charset="utf-8" /><title>Unable to Generate Reports</title></head>
<body><p>Unable to generate any selected QR Punch Walk reports.</p></body>
</html>`);
        return { generatedCount: 0, failedUnits };
      }

      const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(projectName ? `${projectName} - QR Punch Walks` : 'QR Punch Walks')}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif; color:#0f172a; margin:0; background:#fff; }
  .unit-page { min-height:10in; break-after:page; page-break-after:always; }
  .unit-page:last-child { break-after:auto; page-break-after:auto; }
  .header { position:relative; background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%); color:#f8fafc; border-radius:14px; padding:22px; margin-bottom:18px; }
  .logo { position:absolute; right:20px; top:18px; width:180px; height:auto; object-fit:contain; filter:brightness(0) invert(1); }
  .title { font-size:28px; font-weight:800; margin:0; }
  .project-line { margin-top:4px; color:#bfdbfe; font-size:15px; font-weight:600; }
  .subtitle { margin-top:8px; color:#dbeafe; font-size:12px; letter-spacing:0.04em; text-transform:uppercase; }
  .qr-wrap { display:flex; gap:24px; align-items:center; border:1px solid #e2e8f0; border-radius:12px; padding:20px; margin-bottom:18px; }
  .qr-wrap img { width:240px; height:240px; }
  .qr-instructions h2 { margin:0 0 8px; font-size:18px; }
  .qr-instructions p { margin:0 0 6px; font-size:13px; color:#334155; line-height:1.5; }
  .qr-url { font-size:11px; color:#64748b; word-break:break-all; margin-top:8px; }
  .card { border:1px solid #e2e8f0; border-radius:12px; padding:16px; }
  .card h3 { margin:0 0 12px; font-size:14px; text-transform:uppercase; letter-spacing:0.04em; color:#334155; }
  table { width:100%; border-collapse:collapse; }
  th, td { font-size:12px; padding:8px; border-bottom:1px solid #e2e8f0; text-align:left; }
  th { background:#f8fafc; color:#334155; font-weight:700; }
  .footnote { margin-top:10px; font-size:10px; color:#64748b; }
</style>
</head>
<body>
  ${pages.join('\n')}

  <script>
    window.onload = function () { window.focus(); window.print(); };
  </script>
</body>
</html>`;

      writeReportWindow(reportWindow, html);
      return { generatedCount: pages.length, failedUnits };
    },
    [],
  );

  const generateUnitTourReport = useCallback(
    async (
      projectId: number,
      unit: UnitTourReportUnit,
      projectName?: string,
    ) => {
      await generateUnitTourReports(projectId, [unit], projectName);
    },
    [generateUnitTourReports],
  );

  return { generateUnitTourReport, generateUnitTourReports };
};
